import type { Corner } from "./corners";
import { projectLap, projectionOrigin, type Point } from "./geo";
import type { AlignedLap, AlignedTrace } from "./trace";

/**
 * Where the lap time actually went, and what the driver did differently there.
 *
 * The point of this module is to *not* list every difference between two laps.
 * Two laps of the same circuit differ everywhere, and a page that says so is a
 * page nobody reads. So the lap is cut into zones, each zone is priced in seconds
 * against the reference lap, and only the two or three most expensive zones are
 * described at all. Within a zone the same rule applies again: a handful of
 * measurements are taken, and only those that differ by more than a threshold
 * worth a driver's attention are mentioned.
 *
 * Selecting what to report is only honest if nothing can be lost by the selection
 * itself, so the zones tile the lap end to end — braking point to braking point,
 * corner and the run out of it together — and their prices sum to the lap's gap.
 * A zone is then reported with that sum split in two, because "Turn 1, +0.44 s,
 * all of it on the run to Turn 2" and "Turn 1, +0.44 s, all of it in the corner"
 * send a driver to opposite ends of the same problem.
 *
 * Everything here is a measurement, and nothing is inferred about intent: if the
 * brake came on 14 m later, it says so, and leaves the driver to decide whether
 * that was the mistake or the plan. The measurement is all this module produces —
 * a kind and the figures behind it. The sentence is assembled from the translation
 * catalogue where it is read, which is what lets the same finding be coaching in
 * English and in French without this file knowing either language.
 */

/** Zones reported, however many corners lost time. */
const MAX_ZONES = 3;
/** Observations per zone. Three is a thing to work on; ten is a wall of text. */
const MAX_CAUSES_PER_ZONE = 3;

/** Below this a zone is noise: grid quantisation and one lap being a metre wide. */
const MIN_ZONE_LOSS_S = 0.05;
/** Below this a lap-to-lap swing says nothing about consistency. */
const MIN_SPREAD_S = 0.08;

/** Pedal percentages: what counts as on rather than resting against the stop. */
const PEDAL_ON_PCT = 8;
const PEDAL_OFF_PCT = 3;
const FULL_THROTTLE_PCT = 92;

/**
 * Shortest run worth measuring as a run rather than as part of the corner's exit.
 */
const MIN_RUN_M = 60;

/** A run is only diagnosed as one once it has actually cost something. */
const MIN_RUN_LOSS_S = 0.03;

/** Above this share of a zone's loss, the zone is *about* that half of itself. */
const EMPHASIS_SHARE = 0.65;

/**
 * Minimum difference worth mentioning, per measurement, in its own unit. These are
 * the numbers that decide whether the coach speaks, so they are set where a driver
 * could plausibly act on the difference rather than where it becomes detectable.
 */
const THRESHOLDS = {
  brakePointM: 6,
  peakBrakePct: 5,
  apexSpeedKph: 2,
  exitSpeedKph: 2,
  topSpeedKph: 3,
  overlapM: 12,
  coastM: 30,
  throttlePointM: 10,
  peakSteeringPct: 8,
  /** Ratio, not a difference: 1.4 means half again as much wheel movement. */
  steeringWorkRatio: 1.4,
  lineM: 1.5,
} as const;

/** Figures a finding puts in its sentence, already rounded to what is worth saying. */
export type CauseValues = Record<string, number | string>;

export interface CoachCause {
  /** Names both the finding and the sentence that reports it. */
  kind: string;
  values: CauseValues;
  /**
   * How far past its own threshold the measurement is. Comparable across kinds
   * only in the loose sense that both are multiples of "worth mentioning", which
   * is enough to order three bullets and not enough to rank them by seconds.
   */
  strength: number;
}

/** Which half of a zone the time actually went in. */
export type ZoneEmphasis = "corner" | "run" | "both";

export interface CoachZone {
  key: string;
  /** Set for a real corner; null where there was no GPS to find corners with. */
  corner: Corner | null;
  /** The corner this zone runs *to*, which is how the run is named. */
  nextCorner: number | null;
  startIndex: number;
  endIndex: number;
  /** Metres along the lap, for chart bands and for naming a cornerless zone. */
  fromM: number;
  toM: number;
  /** Seconds the coached lap gave away here against the reference. */
  lostSeconds: number;
  /** Of that, what went in the corner itself and what went on the run after it. */
  corneringSeconds: number;
  runSeconds: number;
  emphasis: ZoneEmphasis;
  causes: CoachCause[];
}

export interface CoachSpread {
  key: string;
  corner: Corner | null;
  nextCorner: number | null;
  startIndex: number;
  endIndex: number;
  fromM: number;
  toM: number;
  /** Gap between the driver's own best and worst run through this zone. */
  spreadSeconds: number;
  lapCount: number;
  note: CoachNote | null;
}

/** The input behind an inconsistency, when one input explains most of it. */
export interface CoachNote {
  kind: "brakePoint" | "apexSpeed";
  values: CauseValues;
}

export interface CoachReport {
  student: AlignedLap;
  reference: AlignedLap;
  /** Total gap over the part of the lap both laps cover. */
  lostSeconds: number;
  zones: CoachZone[];
  /** Share of the total gap the reported zones account for, 0..1. */
  covered: number;
  /** Only populated with three or more laps of the same driver on screen. */
  spreads: CoachSpread[];
}

/**
 * Prices every corner, keeps the expensive ones, and says what was different
 * there.
 *
 * `corners` comes from the reference lap so that a zone means the same stretch of
 * track for every lap being compared.
 */
export function coach(
  aligned: AlignedTrace,
  corners: Corner[],
  student: AlignedLap,
  reference: AlignedLap,
  distanceStepM: number,
): CoachReport | null {
  if (student.key === reference.key) return null;
  if (!Number.isFinite(distanceStepM) || distanceStepM <= 0) return null;

  const length = aligned.distance.length;
  if (length === 0) return null;

  const segments = zoneBounds(corners, length, distanceStepM);
  if (segments.length === 0) return null;

  const lines = lineOffsets(student, reference, corners);

  const priced = segments.map((segment) => {
    const lost = lostBetween(student, reference, segment.startIndex, segment.endIndex);
    return {
      segment,
      lost,
      split: splitLoss(student, reference, segment, distanceStepM),
    };
  });

  // Over the stretch both laps cover, not the whole axis: two laps of the same
  // session start at different buckets, and asking for the gap at a point one of
  // them has not reached yet answers nothing.
  const shared = sharedRange(student, reference);
  const lostSeconds = shared
    ? lostBetween(student, reference, shared.startIndex, shared.endIndex)
    : null;

  const worst = priced
    .filter((entry) => (entry.lost ?? 0) >= MIN_ZONE_LOSS_S)
    .sort((a, b) => (b.lost as number) - (a.lost as number))
    .slice(0, MAX_ZONES);

  const zones = worst.map(({ segment, lost, split }) => ({
    key: segment.key,
    corner: segment.corner,
    nextCorner: segment.nextCorner,
    startIndex: segment.startIndex,
    endIndex: segment.endIndex,
    fromM: aligned.distance[segment.startIndex],
    toM: aligned.distance[segment.endIndex],
    lostSeconds: lost as number,
    corneringSeconds: split.cornering,
    runSeconds: split.run,
    emphasis: emphasis(split),
    causes: diagnose(segment, student, reference, lines, distanceStepM, split),
  }));

  const explained = zones.reduce((total, zone) => total + zone.lostSeconds, 0);

  return {
    student,
    reference,
    lostSeconds: lostSeconds ?? 0,
    zones,
    covered: lostSeconds && lostSeconds > 0 ? Math.min(1, explained / lostSeconds) : 0,
    spreads: spreads(aligned, segments, student, distanceStepM).map((spread) => ({
      ...spread,
      fromM: aligned.distance[spread.startIndex],
      toM: aligned.distance[spread.endIndex],
    })),
  };
}

/** The first and last grid point where both laps have a time recorded. */
function sharedRange(
  student: AlignedLap,
  reference: AlignedLap,
): { startIndex: number; endIndex: number } | null {
  const length = Math.min(student.time.length, reference.time.length);
  let startIndex = -1;
  let endIndex = -1;

  for (let index = 0; index < length; index += 1) {
    if (student.time[index] === null || reference.time[index] === null) continue;
    if (startIndex < 0) startIndex = index;
    endIndex = index;
  }

  return startIndex < 0 || endIndex <= startIndex ? null : { startIndex, endIndex };
}

interface Segment {
  key: string;
  corner: Corner | null;
  /** Number of the corner this zone leads to, for naming the run. */
  nextCorner: number | null;
  startIndex: number;
  /** Where the corner ends and the run to the next one begins. */
  cornerEndIndex: number;
  endIndex: number;
}

/** Fallback zone length where there is no GPS, and so no corners. */
const BLIND_SEGMENT_M = 250;

/**
 * Cuts the lap into zones, one per corner, each running from that corner's
 * braking point to the next corner's braking point.
 *
 * The zones tile the lap: every metre belongs to exactly one, and their losses
 * therefore sum to the lap's. That is the whole point. An earlier version gave
 * each corner its exit plus a fixed 200 m and stopped there, which left the rest
 * of every straight in no zone at all — so a lap that lost half a second down the
 * back straight was told its worst corner cost 0.21 s, and the half second was
 * never mentioned. A zone being long is not a problem; time going missing is.
 *
 * A corner is not paid for at its exit anyway. A poor exit is paid for over the
 * whole straight that follows, so crediting that straight to the corner that
 * launched onto it is also the more useful attribution — provided the report then
 * says which half of the zone the time actually went in, which `splitLoss` does.
 */
function zoneBounds(corners: Corner[], length: number, stepM: number): Segment[] {
  const last = length - 1;

  if (corners.length === 0) {
    const span = Math.max(2, Math.round(BLIND_SEGMENT_M / stepM));
    const segments: Segment[] = [];
    for (let start = 0; start < last; start += span) {
      const end = Math.min(last, start + span - 1);
      if (end - start < 2) break;
      segments.push({
        key: `d${start}`,
        corner: null,
        nextCorner: null,
        startIndex: start,
        cornerEndIndex: end,
        endIndex: end,
      });
    }
    return segments;
  }

  const segments: Segment[] = [];

  // The run from the line to the first braking point is the *previous* lap's
  // corner exit, so nothing here can explain it. It is still track, and time lost
  // on it still has to appear somewhere rather than falling out of the accounting.
  const opening = Math.max(0, corners[0].focusStartIndex);
  if (opening > 0) {
    segments.push({
      key: "lead",
      corner: null,
      nextCorner: corners[0].number,
      startIndex: 0,
      cornerEndIndex: 0,
      endIndex: opening - 1,
    });
  }

  corners.forEach((corner, index) => {
    const next = corners[index + 1];
    const start = Math.max(0, corner.focusStartIndex);
    const end = next ? Math.max(start, next.focusStartIndex - 1) : last;
    segments.push({
      key: `t${corner.number}`,
      corner,
      nextCorner: next ? next.number : null,
      startIndex: start,
      cornerEndIndex: Math.min(end, Math.max(start, corner.endIndex)),
      endIndex: end,
    });
  });

  return segments;
}

interface Split {
  cornering: number;
  run: number;
  /** Length of the run in metres, which decides whether it is one at all. */
  runM: number;
}

/** How a zone's loss divides between the corner and the run out of it. */
function splitLoss(
  student: AlignedLap,
  reference: AlignedLap,
  segment: Segment,
  stepM: number,
): Split {
  const cornering =
    segment.cornerEndIndex > segment.startIndex
      ? (lostBetween(student, reference, segment.startIndex, segment.cornerEndIndex) ?? 0)
      : 0;
  const run =
    segment.endIndex > segment.cornerEndIndex
      ? (lostBetween(student, reference, segment.cornerEndIndex, segment.endIndex) ?? 0)
      : 0;
  return {
    cornering,
    run,
    runM: (segment.endIndex - segment.cornerEndIndex) * stepM,
  };
}

function emphasis(split: Split): ZoneEmphasis {
  const positive = Math.max(0, split.cornering) + Math.max(0, split.run);
  if (positive <= 0) return "corner";
  if (split.run / positive >= EMPHASIS_SHARE) return "run";
  if (split.cornering / positive >= EMPHASIS_SHARE) return "corner";
  return "both";
}

/**
 * Seconds the student gave away between two points, from the elapsed-time channel
 * both laps carry. Positive means the student lost time through the stretch.
 */
function lostBetween(
  student: AlignedLap,
  reference: AlignedLap,
  startIndex: number,
  endIndex: number,
): number | null {
  const a = delta(student, reference, startIndex, 1);
  const b = delta(student, reference, endIndex, -1);
  if (a === null || b === null) return null;
  return b - a;
}

/** Search span when the exact bucket is missing from one of the two laps. */
const NEAREST_WINDOW = 12;

/** The gap between the laps at one point, hunting for the nearest usable bucket. */
function delta(
  student: AlignedLap,
  reference: AlignedLap,
  index: number,
  direction: 1 | -1,
): number | null {
  for (let step = 0; step <= NEAREST_WINDOW; step += 1) {
    const at = index + step * direction;
    if (at < 0 || at >= student.time.length) break;
    const mine = student.time[at];
    const theirs = reference.time[at];
    if (mine !== null && theirs !== null) return mine - theirs;
  }
  return null;
}

interface ZoneMetrics {
  brakeStartIndex: number | null;
  peakBrake: number | null;
  minSpeed: number | null;
  minSpeedIndex: number | null;
  exitSpeed: number | null;
  overlapM: number;
  coastM: number;
  fullThrottleIndex: number | null;
  peakSteering: number | null;
  steeringWork: number | null;
  gearAtSlowest: number | null;
}

function measure(
  lap: AlignedLap,
  startIndex: number,
  endIndex: number,
  stepM: number,
): ZoneMetrics {
  const brake = lap.channels.brake ?? [];
  const throttle = lap.channels.throttle ?? [];
  const speed = lap.channels.speed ?? [];
  const steering = lap.channels.steering ?? [];
  const gear = lap.channels.gear ?? [];

  let brakeStartIndex: number | null = null;
  let peakBrake: number | null = null;
  let minSpeed: number | null = null;
  let minSpeedIndex: number | null = null;
  let overlap = 0;
  let coast = 0;
  let peakSteering: number | null = null;
  let steeringWork = 0;
  let previousSteering: number | null = null;

  for (let index = startIndex; index <= endIndex; index += 1) {
    const b = brake[index] ?? null;
    const t = throttle[index] ?? null;
    const v = speed[index] ?? null;
    const s = steering[index] ?? null;

    if (b !== null) {
      if (peakBrake === null || b > peakBrake) peakBrake = b;
      if (brakeStartIndex === null && b >= PEDAL_ON_PCT) brakeStartIndex = index;
    }
    if (v !== null && (minSpeed === null || v < minSpeed)) {
      minSpeed = v;
      minSpeedIndex = index;
    }
    if (b !== null && t !== null) {
      if (b >= PEDAL_ON_PCT && t >= PEDAL_ON_PCT) overlap += stepM;
      if (b < PEDAL_OFF_PCT && t < PEDAL_OFF_PCT) coast += stepM;
    }
    if (s !== null) {
      const magnitude = Math.abs(s);
      if (peakSteering === null || magnitude > peakSteering) peakSteering = magnitude;
      if (previousSteering !== null) steeringWork += Math.abs(s - previousSteering);
      previousSteering = s;
    }
  }

  // Full throttle is only meaningful once the car is past the slowest point:
  // before that it is the run up to the braking zone.
  let fullThrottleIndex: number | null = null;
  const fromIndex = minSpeedIndex ?? startIndex;
  for (let index = fromIndex; index <= endIndex; index += 1) {
    const t = throttle[index] ?? null;
    if (t !== null && t >= FULL_THROTTLE_PCT) {
      fullThrottleIndex = index;
      break;
    }
  }

  return {
    brakeStartIndex,
    peakBrake,
    minSpeed,
    minSpeedIndex,
    exitSpeed: speed[endIndex] ?? null,
    overlapM: overlap,
    coastM: coast,
    fullThrottleIndex,
    peakSteering,
    steeringWork: previousSteering === null ? null : steeringWork,
    gearAtSlowest: minSpeedIndex === null ? null : (gear[minSpeedIndex] ?? null),
  };
}

/**
 * What a run out of a corner is worth: the speed it starts at, the speed it
 * reaches, and the speed it arrives at the next braking point with.
 */
interface RunMetrics {
  entrySpeed: number | null;
  topSpeed: number | null;
}

function measureRun(lap: AlignedLap, startIndex: number, endIndex: number): RunMetrics {
  const speed = lap.channels.speed ?? [];

  let topSpeed: number | null = null;
  for (let index = startIndex; index <= endIndex; index += 1) {
    const value = speed[index] ?? null;
    if (value !== null && (topSpeed === null || value > topSpeed)) topSpeed = value;
  }

  return { entrySpeed: nearest(speed, startIndex), topSpeed };
}

/** First value at or after an index, since any single bucket may be a gap. */
function nearest(values: Array<number | null>, index: number): number | null {
  for (let step = 0; step <= NEAREST_WINDOW; step += 1) {
    const value = values[index + step];
    if (value !== null && value !== undefined) return value;
  }
  return null;
}

/** Signed metres from the reference line, positive to the outside of the corner. */
type LineOffsets = Array<number | null> | null;

/**
 * How far the student's path runs from the reference's, per grid point.
 *
 * Measured to the nearest point on the reference path rather than to the point
 * with the same index, because the x axis here is distance travelled: a wider
 * line covers more ground, so the same index is a different place on the track,
 * and subtracting the two positions directly would read that lag as sideways
 * displacement. Sign comes from which side of the reference's direction of travel
 * the student sits, then is flipped for right-handers so that positive always
 * means the outside of the corner.
 */
function lineOffsets(
  student: AlignedLap,
  reference: AlignedLap,
  corners: Corner[],
): LineOffsets {
  const origin = projectionOrigin(reference);
  if (!origin) return null;
  const mine = projectLap(student, origin);
  const theirs = projectLap(reference, origin);
  if (!mine || !theirs) return null;

  const turnsLeft = leftHandedAt(corners, mine.length);
  const out = new Array<number | null>(mine.length).fill(null);
  const window = 40;

  for (let index = 0; index < mine.length; index += 1) {
    const here = mine[index];
    if (!here) continue;

    let bestDistance = Infinity;
    let best = -1;
    for (
      let probe = Math.max(0, index - window);
      probe <= Math.min(theirs.length - 1, index + window);
      probe += 1
    ) {
      const candidate = theirs[probe];
      if (!candidate) continue;
      const distance = hypot(here, candidate);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = probe;
      }
    }
    if (best < 0) continue;

    const tangent = tangentAt(theirs, best);
    if (!tangent) continue;

    const anchor = theirs[best] as Point;
    // Cross product of the direction of travel with the offset: positive is to
    // the left of the reference car, in a y-down frame.
    const cross =
      tangent.x * (here.y - anchor.y) - tangent.y * (here.x - anchor.x);
    const side = cross >= 0 ? 1 : -1;
    const outside = turnsLeft[index] === null ? side : turnsLeft[index] ? -side : side;
    out[index] = bestDistance * outside;
  }

  return out;
}

/** True inside a left-hander, false inside a right, null between corners. */
function leftHandedAt(corners: Corner[], length: number): Array<boolean | null> {
  const out = new Array<boolean | null>(length).fill(null);
  for (const corner of corners) {
    for (
      let index = Math.max(0, corner.focusStartIndex);
      index <= Math.min(length - 1, corner.endIndex);
      index += 1
    ) {
      out[index] = corner.direction === "left";
    }
  }
  return out;
}

function hypot(a: Point, b: Point): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

function tangentAt(points: Array<Point | null>, index: number): Point | null {
  const before = previousPoint(points, index);
  const after = nextPoint(points, index);
  if (!before || !after) return null;
  const dx = after.x - before.x;
  const dy = after.y - before.y;
  const size = Math.sqrt(dx * dx + dy * dy);
  if (size < 1e-6) return null;
  return { x: dx / size, y: dy / size };
}

function previousPoint(points: Array<Point | null>, index: number): Point | null {
  for (let at = index; at >= Math.max(0, index - 5); at -= 1) {
    if (points[at]) return points[at];
  }
  return null;
}

function nextPoint(points: Array<Point | null>, index: number): Point | null {
  for (let at = index + 1; at <= Math.min(points.length - 1, index + 5); at += 1) {
    if (points[at]) return points[at];
  }
  return null;
}

/**
 * Turns the difference between two sets of measurements into advice, keeping only
 * the findings that clear their own threshold and only the strongest few of those.
 */
function diagnose(
  segment: Segment,
  student: AlignedLap,
  reference: AlignedLap,
  lines: LineOffsets,
  stepM: number,
  split: Split,
): CoachCause[] {
  const mine = measure(student, segment.startIndex, segment.cornerEndIndex, stepM);
  const theirs = measure(reference, segment.startIndex, segment.cornerEndIndex, stepM);
  const causes: CoachCause[] = [];

  // A zone now spans a corner and the run out of it, and a finding about one is
  // not worth reading when the time went in the other. Clearing its own threshold
  // still decides whether a finding is *true*; the share decides what it is worth
  // saying first.
  const positive = Math.max(0, split.cornering) + Math.max(0, split.run);
  const cornerShare = positive > 0 ? Math.max(0, split.cornering) / positive : 1;
  const runShare = positive > 0 ? Math.max(0, split.run) / positive : 0;

  const add = (
    kind: string,
    strength: number,
    values: CauseValues,
    share = cornerShare,
  ) => {
    if (strength >= 1) causes.push({ kind, values, strength: strength * (0.5 + share) });
  };

  // --- braking ---------------------------------------------------------------

  if (mine.brakeStartIndex !== null && theirs.brakeStartIndex !== null) {
    const metres = (mine.brakeStartIndex - theirs.brakeStartIndex) * stepM;
    if (metres > 0) {
      add("brake_late", metres / THRESHOLDS.brakePointM, { metres: round(metres) });
    } else if (metres < 0) {
      add("brake_early", -metres / THRESHOLDS.brakePointM, { metres: round(-metres) });
    }
  }

  if (mine.peakBrake !== null && theirs.peakBrake !== null) {
    const difference = mine.peakBrake - theirs.peakBrake;
    if (difference > 0) {
      add("brake_hard", difference / THRESHOLDS.peakBrakePct, {
        percent: round(difference),
      });
    } else if (difference < 0) {
      add("brake_soft", -difference / THRESHOLDS.peakBrakePct, {
        percent: round(-difference),
      });
    }
  }

  if (mine.overlapM > theirs.overlapM) {
    const extra = mine.overlapM - theirs.overlapM;
    add("overlap", extra / THRESHOLDS.overlapM, { metres: round(mine.overlapM) });
  }

  if (mine.coastM > theirs.coastM) {
    const extra = mine.coastM - theirs.coastM;
    add("coast", extra / THRESHOLDS.coastM, {
      metres: round(mine.coastM),
      extra: round(extra),
    });
  }

  // --- speed carried ---------------------------------------------------------

  if (mine.minSpeed !== null && theirs.minSpeed !== null) {
    const difference = theirs.minSpeed - mine.minSpeed;
    if (difference > 0) {
      add("apex_speed", difference / THRESHOLDS.apexSpeedKph, {
        kph: round(difference),
      });
    }
  }

  if (mine.fullThrottleIndex !== null && theirs.fullThrottleIndex !== null) {
    const metres = (mine.fullThrottleIndex - theirs.fullThrottleIndex) * stepM;
    if (metres > 0) {
      add("throttle_late", metres / THRESHOLDS.throttlePointM, {
        metres: round(metres),
      });
    }
  }

  // --- hands ----------------------------------------------------------------

  if (mine.peakSteering !== null && theirs.peakSteering !== null) {
    const difference = mine.peakSteering - theirs.peakSteering;
    if (difference > 0) {
      add("steering_peak", difference / THRESHOLDS.peakSteeringPct, {
        percent: round(difference),
      });
    }
  }

  if (
    mine.steeringWork !== null &&
    theirs.steeringWork !== null &&
    theirs.steeringWork > 1
  ) {
    const ratio = mine.steeringWork / theirs.steeringWork;
    if (ratio > 1) {
      add("steering_busy", ratio / THRESHOLDS.steeringWorkRatio, {
        ratio: Math.round(ratio * 10) / 10,
      });
    }
  }

  if (
    mine.gearAtSlowest !== null &&
    theirs.gearAtSlowest !== null &&
    Math.round(mine.gearAtSlowest) !== Math.round(theirs.gearAtSlowest)
  ) {
    add("gear", 1, {
      mineGear: Math.round(mine.gearAtSlowest),
      theirsGear: Math.round(theirs.gearAtSlowest),
    });
  }

  // --- the run out of the corner ---------------------------------------------
  // Only once the run is long enough to be a straight and has actually cost
  // something, otherwise every corner exit collects two more bullets it does not
  // need.

  if (split.runM >= MIN_RUN_M && split.run >= MIN_RUN_LOSS_S) {
    const mineRun = measureRun(student, segment.cornerEndIndex, segment.endIndex);
    const theirsRun = measureRun(reference, segment.cornerEndIndex, segment.endIndex);

    if (mineRun.entrySpeed !== null && theirsRun.entrySpeed !== null) {
      const difference = theirsRun.entrySpeed - mineRun.entrySpeed;
      if (difference > 0) {
        add(
          "exit_speed",
          difference / THRESHOLDS.exitSpeedKph,
          { kph: round(difference), metres: Math.round(split.runM) },
          runShare,
        );
      }
    }

    if (mineRun.topSpeed !== null && theirsRun.topSpeed !== null) {
      const difference = theirsRun.topSpeed - mineRun.topSpeed;
      if (difference > 0) {
        add(
          "run_speed",
          difference / THRESHOLDS.topSpeedKph,
          { kph: round(difference), seconds: round(split.run) },
          runShare,
        );
      }
    }
  }

  // --- line ------------------------------------------------------------------

  if (lines) {
    const line = worstOffset(lines, segment.startIndex, segment.cornerEndIndex, mine.minSpeedIndex);
    if (line) {
      add(
        line.metres > 0 ? "line_wide" : "line_tight",
        Math.abs(line.metres) / THRESHOLDS.lineM,
        { metres: round(Math.abs(line.metres)), phase: line.phase },
      );
    }
  }

  return causes.sort((a, b) => b.strength - a.strength).slice(0, MAX_CAUSES_PER_ZONE);
}

/** Which part of the corner a difference falls in. */
type Phase = "turnIn" | "apex" | "exit";

/** The single largest departure from the reference line, and where it happens. */
function worstOffset(
  lines: Array<number | null>,
  startIndex: number,
  endIndex: number,
  slowestIndex: number | null,
): { metres: number; phase: Phase } | null {
  const apex = slowestIndex ?? Math.round((startIndex + endIndex) / 2);
  let worst = 0;
  let at = -1;

  for (let index = startIndex; index <= endIndex; index += 1) {
    const value = lines[index];
    if (value === null || value === undefined) continue;
    if (Math.abs(value) > Math.abs(worst)) {
      worst = value;
      at = index;
    }
  }

  if (at < 0) return null;
  const phase: Phase = at < apex - 2 ? "turnIn" : at > apex + 2 ? "exit" : "apex";
  return { metres: worst, phase };
}

/**
 * How much the driver's own laps disagree with each other, zone by zone.
 *
 * Separate from the gap to the reference because it answers a different question:
 * not "where is someone else quicker" but "where do I not yet know what I am
 * doing". Only the coached driver's own laps count, and only with three of them —
 * two laps have a spread by definition and it means nothing.
 */
function spreads(
  aligned: AlignedTrace,
  segments: Segment[],
  student: AlignedLap,
  stepM: number,
): Array<Omit<CoachSpread, "fromM" | "toM">> {
  const own = aligned.laps.filter((lap) => lap.driver === student.driver);
  if (own.length < 3) return [];

  const found: Array<Omit<CoachSpread, "fromM" | "toM">> = [];

  for (const segment of segments) {
    const times: number[] = [];
    for (const lap of own) {
      const start = nearestTime(lap, segment.startIndex, 1);
      const end = nearestTime(lap, segment.endIndex, -1);
      if (start === null || end === null) continue;
      times.push(end - start);
    }
    if (times.length < 3) continue;

    const spread = Math.max(...times) - Math.min(...times);
    if (spread < MIN_SPREAD_S) continue;

    found.push({
      key: segment.key,
      corner: segment.corner,
      nextCorner: segment.nextCorner,
      startIndex: segment.startIndex,
      endIndex: segment.endIndex,
      spreadSeconds: spread,
      lapCount: times.length,
      note: variableInput(own, segment, stepM),
    });
  }

  return found.sort((a, b) => b.spreadSeconds - a.spreadSeconds).slice(0, 2);
}

/** Whichever of the driver's inputs moves most from lap to lap, if either does. */
function variableInput(
  laps: AlignedLap[],
  segment: Segment,
  stepM: number,
): CoachNote | null {
  const brakePoints: number[] = [];
  const apexSpeeds: number[] = [];

  for (const lap of laps) {
    const metrics = measure(lap, segment.startIndex, segment.cornerEndIndex, stepM);
    if (metrics.brakeStartIndex !== null) brakePoints.push(metrics.brakeStartIndex);
    if (metrics.minSpeed !== null) apexSpeeds.push(metrics.minSpeed);
  }

  const brakeSwing =
    brakePoints.length >= 3
      ? (Math.max(...brakePoints) - Math.min(...brakePoints)) * stepM
      : 0;
  const speedSwing =
    apexSpeeds.length >= 3 ? Math.max(...apexSpeeds) - Math.min(...apexSpeeds) : 0;

  if (brakeSwing >= THRESHOLDS.brakePointM) {
    return { kind: "brakePoint", values: { metres: round(brakeSwing) } };
  }
  if (speedSwing >= THRESHOLDS.apexSpeedKph * 2) {
    return { kind: "apexSpeed", values: { kph: round(speedSwing) } };
  }
  return null;
}

function nearestTime(lap: AlignedLap, index: number, direction: 1 | -1): number | null {
  for (let step = 0; step <= NEAREST_WINDOW; step += 1) {
    const at = index + step * direction;
    if (at < 0 || at >= lap.time.length) break;
    const value = lap.time[at];
    if (value !== null) return value;
  }
  return null;
}

/**
 * Rounded to the precision a driver can act on, and left as a number so that the
 * language being read decides whether it prints 3.2 or 3,2.
 */
function round(value: number): number {
  return Math.abs(value) >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
}
